<template>
  <!-- Nothing at all for a lab outside the beta. An always-present control that
       always refuses is worse than no control: it advertises a feature the user
       cannot have and costs a tap to find that out. -->
  <div v-if="available" class="sa-voice-slot">
    <!-- ONE button, two homes. `disabled` is the whole trick: when the launcher
         belongs in the search bar this teleport does nothing and the button
         renders right here, exactly as it always has; when it belongs in the
         corner the same element moves to <body>.

         It has to leave the bar to float. The bar is `position: sticky` with a
         z-index, which makes it a stacking context, so a `position: fixed` disc
         left inside it would be fixed to the viewport but still painted inside
         the bar's layer — under anything on the page stacked above 50, and
         clipped the moment the bar ever gains an `overflow`. Same reasoning as
         the dock's own teleport below. -->
    <teleport to="body" :disabled="placement !== 'floating'">
      <button
        v-if="placement !== 'hidden'"
        ref="launcher"
        type="button"
        class="sa-voice-inline"
        :class="{
          'sa-voice-inline-spent': exhausted,
          'sa-voice-float': placement === 'floating',
          'sa-voice-live': live,
        }"
        :aria-label="label"
        :title="label"
        @click="open"
      >
        <!-- The listening ring. Inside the disc's own edge rather than around
             it, because the inline placement is 52 px in a 52 px bar: a halo
             drawn outside would spill past the bar onto the white page. -->
        <span class="sa-voice-ring" aria-hidden="true"></span>
        <svg
          class="sa-voice-glyph"
          viewBox="0 0 24 24"
          width="26"
          height="26"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
        </svg>
      </button>
    </teleport>

    <!-- Teleported to <body> on purpose. This control sits inside the search
         bar, which is a sticky, z-indexed container and therefore a stacking
         context — left in place the dock would be trapped beneath the nav
         overlay (9000) rather than sitting over it. It also keeps the dock
         outside `#app`, which is the element the panel pushes: a dock inside
         the thing it is padding would push itself. -->
    <teleport to="body">
      <voice-dock
        v-if="modalOpen"
        :ref="captureDock"
        @close="modalOpen = false"
        @follow-to-position="followToPosition"
      />
    </teleport>
  </div>
</template>

<script>
import { markRaw } from "vue";
import VoiceDock from "./VoiceDock.vue";
import { micPlacement } from "../../utils/voiceDockLayout.js";

/**
 * The primary pointer, read the same way `loadPrefs` in the dock reads it.
 *
 * Guarded rather than assumed: this module is compiled and rendered without a
 * browser by `scripts/test-voice-focus.js`, and a bare `window` reference would
 * make the launcher untestable outside one.
 */
function readCoarsePointer() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    /* A browser that refuses the query is one we know nothing about; the width
       test below still decides, and it is the stronger of the two. */
    return false;
  }
}

/** `NaN` rather than a guessed number: `micPlacement` fails to the corner. */
function readViewportWidth() {
  if (typeof window === "undefined") return NaN;
  return window.innerWidth || NaN;
}

export default {
  name: "VoiceControl",
  components: { VoiceDock },
  emits: ["follow-to-position"],
  data() {
    return {
      modalOpen: false,
      // The open dock itself, so the launcher can read the one fact it needs
      // from it. `markRaw` because this is a component instance, not state:
      // Vue must not wrap the proxy in a second reactive proxy on every read.
      dock: null,
      coarsePointer: readCoarsePointer(),
      viewportWidth: readViewportWidth(),
    };
  },
  computed: {
    credits() {
      return this.$store.state.voiceCredits;
    },
    // Absent credits means the check has not come back yet, or failed. Either
    // way the button stays hidden rather than appearing and then vanishing.
    available() {
      return !!this.credits && this.credits.voiceEnabled === true;
    },
    exhausted() {
      return !!this.credits && this.credits.exhausted === true;
    },
    label() {
      if (this.exhausted) return "Voice control — no minutes left this period";
      return "Start voice control";
    },
    /** 'inline' | 'floating' | 'hidden'. The rule itself lives in the layout module. */
    placement() {
      return micPlacement({
        coarsePointer: this.coarsePointer,
        viewportWidth: this.viewportWidth,
        dockOpen: this.modalOpen,
      });
    },
    /**
     * Whether a session can still hear, asked of the dock rather than tracked
     * here. The dock owns the session and already computes this for its own
     * state word and footer, so reading it is the only way the launcher and the
     * dock cannot end up disagreeing about whether the microphone is open.
     *
     * Reachable in the inline placement, where the launcher stays in the bar
     * while the panel is open beside it. The floating disc hides whenever the
     * dock is up, so it never paints the ring — it cannot, because closing the
     * dock is what ends the session.
     */
    live() {
      return !!(this.dock && this.dock.sessionLive);
    },
  },
  methods: {
    followToPosition(destination) {
      this.modalOpen = false;
      this.$emit("follow-to-position", destination);
    },

    captureDock(vm) {
      this.dock = vm ? markRaw(vm) : null;
    },

    measure() {
      this.viewportWidth = readViewportWidth();
      // Re-read on every resize rather than subscribing to the media query
      // separately: a pointer change (a tablet gaining a mouse, a window
      // dragged to a second screen) arrives with a resize in every case that
      // moves this button, and one listener cannot fall out of step with the
      // other.
      this.coarsePointer = readCoarsePointer();
    },

    open() {
      if (this.exhausted) {
        // Answered locally. The mint endpoint would refuse this with the same
        // message; asking it first would just be a slower way to say no.
        this.$store.dispatch("notify", {
          uiState: "warning",
          title: "Voice not started.",
          detail: "This lab has used all its voice minutes this period.",
          next: "Ask your lab admin to raise the allowance.",
          key: "voice|exhausted",
        });
        return;
      }
      this.modalOpen = true;
    },
  },
  watch: {
    /**
     * The floating disc unmounts when the dock opens, so the element that had
     * focus is gone and a keyboard user is left on <body>. Putting focus back
     * on the launcher when the dock closes returns them to where they were.
     * The inline placement never leaves, so this is a no-op there.
     */
    modalOpen(open) {
      if (open) return;
      this.$nextTick(() => {
        this.$refs.launcher?.focus?.();
      });
    },
  },
  mounted() {
    // Swallowed on purpose: a lab that has never heard of voice must not get an
    // error card on every visit to a page carrying the search bar just because
    // this endpoint is not deployed yet. No credits means no button, and
    // nothing said.
    this.$store.dispatch("fetchVoiceCredits").catch(() => {});
    this.measure();
    window.addEventListener("resize", this.measure);
    window.addEventListener("orientationchange", this.measure);
  },
  unmounted() {
    window.removeEventListener("resize", this.measure);
    window.removeEventListener("orientationchange", this.measure);
  },
};
</script>

<style scoped>
/* Plain CSS. public/assets/styles.css is pre-compiled and committed, so a
   Tailwind class not already in it does nothing at runtime.

   The palette is the app's own, read off what is already on screen: #0c4a6e is
   the sky-900 of the search bar this button sits in and of the dock's ink,
   #0284c7 is the sky-600 the dock already uses for a live reading, and #bae6fd
   is the sky-200 of its rules. The disc is those three and nothing else, so it
   reads as part of this app rather than as a floating action button borrowed
   from somewhere with a different colour. */

/* No longer a floating FAB: this is a flex child of the search bar's row, so
   it must not grow, shrink or add a line box of its own. Still true when the
   button has teleported out of it — the empty slot is then zero wide. */
.sa-voice-slot {
  flex: none;
  display: flex;
  align-items: center;
}

.sa-voice-inline {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  /* 52 px, the preferred gloved-hand target. The search bar grows to fit it,
     and only for labs that actually get the button. The "Voice" word that the
     FAB carried is gone — it does not fit a toolbar this tall — so the mic
     glyph carries the meaning and the accessible name is on aria-label. */
  min-width: 52px;
  min-height: 52px;
  margin-right: 0.25rem;
  padding: 0;

  border: 0;
  border-radius: 9999px;
  /* Was transparent, which made the control a glyph rather than a button. A
     disc lifted off the bar by one shade of the app's own blue is findable at
     a glance from a metre away, which is the distance this is pressed from. */
  background-image: linear-gradient(145deg, #0284c7 0%, #0c4a6e 70%);
  color: #ffffff;
  font-family: Montserrat, sans-serif;
  cursor: pointer;

  /* Both of these are inside the disc: a hairline rim that separates it from
     the sky-900 bar, and a highlight along the top edge that gives the
     gradient a light source. Nothing is cast outwards here — in the bar there
     is no page underneath to cast onto. */
  box-shadow:
    inset 0 0 0 1px rgba(186, 230, 253, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);

  transition:
    transform 120ms ease,
    box-shadow 120ms ease,
    filter 120ms ease;
}

.sa-voice-glyph {
  position: relative;
}

/* The ring is drawn always and coloured only when live, so switching states
   moves opacity rather than layout: no reflow under a thumb that is already
   on the button. */
.sa-voice-ring {
  position: absolute;
  top: 4px;
  right: 4px;
  bottom: 4px;
  left: 4px;
  border: 2px solid transparent;
  border-radius: 9999px;
  pointer-events: none;
}

/* Listening. A slow breath rather than the usual alert pulse: this state can
   be true for minutes at a time on a screen someone is working next to, and a
   1 s throb in the corner of the eye for that long is a distraction rather
   than a reading. */
.sa-voice-live .sa-voice-ring {
  border-color: rgba(186, 230, 253, 0.9);
  animation: sa-voice-breathe 2600ms ease-in-out infinite;
}

.sa-voice-live {
  filter: saturate(1.15) brightness(1.06);
}

@keyframes sa-voice-breathe {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.32;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
}

/* Pressed. Pushed in rather than raised: the highlight goes, a shadow lands
   across the top of the disc, and the whole thing loses 6 %. */
.sa-voice-inline:active {
  transform: scale(0.94);
  box-shadow:
    inset 0 0 0 1px rgba(186, 230, 253, 0.28),
    inset 0 3px 8px rgba(3, 26, 45, 0.5);
}

/* Only where there is a pointer to hover with. */
@media (hover: hover) {
  .sa-voice-inline:hover {
    filter: brightness(1.1);
  }
}

/* Out of minutes. Still visible and still tappable — it explains itself when
   pressed — but no longer presented as the ready primary action. Slate rather
   than a faded blue, the same #64748b the dock's hold button wears when it is
   not live, so "grey means it cannot hear you" means one thing across the
   feature. */
.sa-voice-inline-spent {
  background-image: linear-gradient(145deg, #94a3b8 0%, #475569 70%);
  box-shadow: inset 0 0 0 1px rgba(226, 232, 240, 0.35);
}

.sa-voice-inline:focus-visible {
  outline: 3px solid #ffffff;
  outline-offset: -3px;
}

/* ------------------------------------------------------------- floating */

/* The same disc, in the corner a thumb can reach without regripping the phone.
   Everything below is about being somewhere else, not about looking different:
   the fill, the rim, the glyph and the ring are the rules above. */
.sa-voice-float {
  position: fixed;
  /* Above every layer the page itself uses (the search bar is 50, the sticky
     top row 49) and below the three that are allowed to cover it: the nav
     overlay at 9000, which is a modal backdrop, the dock at 9500, and the
     notifications at 10000, which carry failed writes and must never be
     obscured by a button. */
  z-index: 8900;

  /* The plain pair first for browsers without `env()`, then the inset-aware
     pair: on a phone with a home indicator, 16 px from the bottom of the glass
     is under the gesture bar, not above it. */
  right: 16px;
  bottom: 16px;
  right: max(16px, env(safe-area-inset-right));
  bottom: max(16px, env(safe-area-inset-bottom));

  /* 56 px: a free-standing target has no neighbouring controls to borrow
     accuracy from, so it takes the larger of the two sizes. */
  width: 56px;
  height: 56px;
  min-width: 56px;
  min-height: 56px;
  margin: 0;

  /* Here there IS a page underneath, so the disc casts onto it — two shadows,
     a tight one for the edge and a wide soft one for the lift, both in the
     app's navy rather than grey so the shadow belongs to the object. */
  box-shadow:
    0 10px 24px rgba(8, 47, 73, 0.34),
    0 2px 6px rgba(8, 47, 73, 0.26),
    inset 0 0 0 1px rgba(186, 230, 253, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);
}

.sa-voice-float:active {
  box-shadow:
    0 4px 10px rgba(8, 47, 73, 0.3),
    inset 0 0 0 1px rgba(186, 230, 253, 0.28),
    inset 0 3px 8px rgba(3, 26, 45, 0.5);
}

/* On white there is room outside the disc, so a live session also gets a halo
   — static, because the ring inside is already the moving part. */
.sa-voice-float.sa-voice-live {
  box-shadow:
    0 0 0 6px rgba(2, 132, 199, 0.16),
    0 10px 24px rgba(8, 47, 73, 0.34),
    0 2px 6px rgba(8, 47, 73, 0.26),
    inset 0 0 0 1px rgba(186, 230, 253, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);
}

/* White would disappear into the page. */
.sa-voice-float:focus-visible {
  outline: 3px solid #0c4a6e;
  outline-offset: 3px;
}

@media (prefers-reduced-motion: reduce) {
  .sa-voice-inline {
    transition: none;
  }

  .sa-voice-inline:active {
    transform: none;
  }

  /* The ring still says "listening", it just says it by being there. */
  .sa-voice-live .sa-voice-ring {
    animation: none;
    opacity: 0.9;
  }
}
</style>
